import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import NavBar from '../components/NavBar';
import { notifyTimerDone, notifyAlarm, notifyEventReminder, isNotificationsEnabled } from '../utils/notifications';
import { buildHolidayMap, HERITAGE_MONTHS } from '../utils/holidays';

const EVENT_COLORS = {
  appointment: '#3b82f6',
  task: '#f59e0b',
  medication: '#ef4444',
  event: '#8b5cf6',
  other: '#6b7280'
};

const Calendar = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('calendar');

  // Form state
  const [form, setForm] = useState({
    title: '', event_type: 'event', start_time: '',
    end_time: '', all_day: false, notes: '',
    location: '', reminder_minutes: null, recurring: null, color: null
  });

  // Timer state
  const [timerH, setTimerH] = useState(0);
  const [timerM, setTimerM] = useState(0);
  const [timerS, setTimerS] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerRemaining, setTimerRemaining] = useState(0);
  const timerRef = useRef(null);

  // Alarm state
  const [alarms, setAlarms] = useState(() => {
    try { return JSON.parse(localStorage.getItem('careguard_alarms') || '[]'); }
    catch { return []; }
  });
  const [alarmTime, setAlarmTime] = useState('');
  const [alarmLabel, setAlarmLabel] = useState('');
  const [ringingAlarm, setRingingAlarm] = useState(null);
  const alarmAudioRef = useRef(null);
  const alarmCheckRef = useRef(null);

  // Clock state
  const [clockTime, setClockTime] = useState(new Date());

  // Clock tick
  useEffect(() => {
    const interval = setInterval(() => setClockTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Calendar integrations state
  const [integrations, setIntegrations] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchIntegrations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/integrations/status`);
      setIntegrations(res.data);
    } catch (err) {
      // silent; user may just not have opened the tab yet
    }
  }, []);

  useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

  // Handle OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('integration');
    if (status === 'connected') {
      toast.success(`${params.get('provider') || 'Calendar'} connected successfully!`);
      fetchIntegrations();
      window.history.replaceState({}, '', '/calendar');
      setActiveTab('integrations');
    } else if (status === 'error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(`Calendar connect failed: ${reason.replace(/_/g, ' ')}`);
      window.history.replaceState({}, '', '/calendar');
      setActiveTab('integrations');
    }
  }, [fetchIntegrations]);

  const connectGoogle = async () => {
    try {
      const res = await axios.get(`${API}/oauth/google-calendar/connect`);
      window.location.href = res.data.authorization_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to start Google connect';
      toast.error(detail);
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar? Pulled events will be removed.')) return;
    try {
      await axios.post(`${API}/calendar/integrations/google/disconnect`);
      toast.success('Google Calendar disconnected');
      fetchIntegrations();
      fetchEvents();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const syncPullGoogle = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/calendar/integrations/google/sync-pull`);
      toast.success(`Pulled ${res.data.pulled} events from Google`);
      fetchIntegrations();
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Pull failed');
    } finally {
      setSyncing(false);
    }
  };

  const syncPushGoogle = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/calendar/integrations/google/sync-push`);
      toast.success(`Pushed ${res.data.pushed} events to Google`);
      fetchIntegrations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Push failed');
    } finally {
      setSyncing(false);
    }
  };

  // Alarm checker
  useEffect(() => {
    alarmCheckRef.current = setInterval(() => {
      const now = new Date();
      const nowStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const active = alarms.find(a => a.active && a.time === nowStr && !a.fired);
      if (active) {
        setRingingAlarm(active);
        setAlarms(prev => {
          const updated = prev.map(a => a.id === active.id ? { ...a, fired: true } : a);
          localStorage.setItem('careguard_alarms', JSON.stringify(updated));
          return updated;
        });
        notifyAlarm(active.label);
      }
    }, 5000);
    return () => clearInterval(alarmCheckRef.current);
  }, [alarms]);

  const fetchEvents = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
      const res = await axios.get(`${API}/calendar/events?start=${start}&end=${end}`);
      setEvents(res.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error('Failed to load events. Please try refreshing the page.');
      }
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Event reminder checker
  useEffect(() => {
    if (!isNotificationsEnabled()) return;
    const reminderCheck = setInterval(() => {
      const now = new Date();
      events.forEach(ev => {
        if (ev.completed || !ev.reminder_minutes || !ev.start_time) return;
        const start = new Date(ev.start_time);
        const diff = (start - now) / 60000; // minutes until event
        if (diff > 0 && diff <= ev.reminder_minutes && diff > ev.reminder_minutes - 1) {
          notifyEventReminder(ev.title, Math.round(diff));
        }
      });
    }, 60000);
    return () => clearInterval(reminderCheck);
  }, [events]);

  // Timer logic
  useEffect(() => {
    if (timerRunning && timerRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimerRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setTimerRunning(false);
            toast.success('Timer finished!');
            notifyTimerDone();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning, timerRemaining]);

  const startTimer = () => {
    const total = timerH * 3600 + timerM * 60 + timerS;
    if (total <= 0) {
      toast.error('Please set a time greater than zero');
      return;
    }
    setTimerRemaining(total);
    setTimerRunning(true);
  };

  const formatTimer = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const addAlarm = () => {
    if (!alarmTime) return;
    const newAlarm = {
      id: Date.now().toString(),
      time: alarmTime,
      label: alarmLabel || 'Alarm',
      active: true,
      fired: false
    };
    const updated = [...alarms, newAlarm];
    setAlarms(updated);
    localStorage.setItem('careguard_alarms', JSON.stringify(updated));
    setAlarmTime('');
    setAlarmLabel('');
    toast.success('Alarm set');
  };

  const removeAlarm = (id) => {
    const updated = alarms.filter(a => a.id !== id);
    setAlarms(updated);
    localStorage.setItem('careguard_alarms', JSON.stringify(updated));
  };

  const dismissAlarm = () => {
    setRingingAlarm(null);
  };

  // Calendar helpers
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(new Date()); };

  const isSameDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  const isToday = (day) => isSameDay(new Date(currentDate.getFullYear(), currentDate.getMonth(), day), new Date());

  const getEventsForDay = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return events.filter(e => {
      const eDate = new Date(e.start_time);
      return isSameDay(eDate, date);
    });
  };

  const getSelectedDayEvents = () => {
    return events.filter(e => isSameDay(new Date(e.start_time), selectedDate)).sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const resetForm = () => {
    setForm({ title: '', event_type: 'event', start_time: '', end_time: '', all_day: false, notes: '', location: '', reminder_minutes: null, recurring: null, color: null });
    setEditingEvent(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}T09:00`;
    setForm({ ...form, title: '', event_type: 'event', start_time: dateStr, end_time: '', all_day: false, notes: '', location: '', reminder_minutes: null, recurring: null, color: null });
    setEditingEvent(null);
    setShowForm(true);
  };

  const openEditForm = (event) => {
    setForm({
      title: event.title, event_type: event.event_type,
      start_time: event.start_time ? event.start_time.slice(0, 16) : '',
      end_time: event.end_time ? event.end_time.slice(0, 16) : '',
      all_day: event.all_day, notes: event.notes || '',
      location: event.location || '',
      reminder_minutes: event.reminder_minutes, recurring: event.recurring,
      color: event.color
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error('Please enter an event title');
      return;
    }
    if (!form.start_time) {
      toast.error('Please select a start time');
      return;
    }
    try {
      const payload = { ...form, start_time: new Date(form.start_time).toISOString() };
      if (form.end_time) payload.end_time = new Date(form.end_time).toISOString();
      else payload.end_time = null;

      if (editingEvent) {
        await axios.put(`${API}/calendar/events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/calendar/events`, payload);
        toast.success('Event created');
      }
      resetForm();
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save event. Please try again.');
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const toggleComplete = async (event) => {
    try {
      await axios.put(`${API}/calendar/events/${event.id}`, { completed: !event.completed });
      fetchEvents();
    } catch {
      toast.error('Failed to update event');
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = [t('calendar.sun'), t('calendar.mon'), t('calendar.tue'), t('calendar.wed'), t('calendar.thu'), t('calendar.fri'), t('calendar.sat')];

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const holidayMap = buildHolidayMap(currentDate.getFullYear());
  const getHolidaysForDay = (day) => {
    const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidayMap.get(key) || [];
  };

  return (
    <div className="dashboard-container" data-testid="calendar-page">
      <NavBar onLogout={onLogout} />

      {/* Ringing alarm overlay */}
      {ringingAlarm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="alarm-ringing">
          <div style={{ background: '#fff', borderRadius: '16px', padding: '2.5rem', textAlign: 'center', maxWidth: '400px', animation: 'pulse 1s infinite' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><i className="fa-solid fa-bell fa-shake" style={{ color: '#ef4444' }}></i></div>
            <h2 style={{ color: '#2c5364', marginBottom: '0.5rem' }}>{t('calendar.alarmRinging')}</h2>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444', marginBottom: '0.5rem' }}>{ringingAlarm.time}</p>
            <p style={{ color: '#6b8899', marginBottom: '1.5rem' }}>{ringingAlarm.label}</p>
            <button className="submit-button" onClick={dismissAlarm} data-testid="dismiss-alarm-btn" style={{ background: '#ef4444' }}>
              <i className="fa-solid fa-bell-slash"></i> {t('calendar.alarmOff')}
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-content page-container">
        <h1 className="page-title" data-testid="calendar-title">{t('calendar.title')}</h1>

        {/* Tab Switcher */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'calendar', label: t('calendar.calendarTab'), icon: 'fa-calendar-days' },
            { key: 'timer', label: t('calendar.timer'), icon: 'fa-hourglass-half' },
            { key: 'alarm', label: t('calendar.alarm'), icon: 'fa-bell' },
            { key: 'clock', label: t('calendar.clock'), icon: 'fa-clock' },
            { key: 'integrations', label: 'Sync', icon: 'fa-link' }
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
              style={{
                padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                fontWeight: activeTab === tab.key ? '700' : '500', fontSize: '0.95rem',
                background: activeTab === tab.key ? '#2c5364' : '#e2e8f0',
                color: activeTab === tab.key ? '#fff' : '#4a6572', transition: 'all 0.2s'
              }}>
              <i className={`fa-solid ${tab.icon}`} style={{ marginRight: '6px' }}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ===== CALENDAR TAB ===== */}
        {activeTab === 'calendar' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.5rem', alignItems: 'start' }}>
            {/* Month Grid */}
            <div className="section-card" data-testid="month-view">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#2c5364' }} data-testid="prev-month">
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <div style={{ textAlign: 'center' }}>
                  <h2 style={{ margin: 0, color: '#2c5364' }}>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
                </div>
                <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#2c5364' }} data-testid="next-month">
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>

              {/* Heritage / awareness months banner */}
              {HERITAGE_MONTHS[currentDate.getMonth()] && (
                <div className="heritage-banner" data-testid="heritage-banner">
                  <div className="heritage-banner-label">
                    <i className="fa-solid fa-ribbon"></i> This Month We Recognize
                  </div>
                  <div className="heritage-banner-list">
                    {HERITAGE_MONTHS[currentDate.getMonth()].map((h, i) => (
                      <span key={i} className="heritage-chip" style={{ borderLeftColor: h.color }} data-testid={`heritage-chip-${i}`}>
                        <span className="heritage-chip-icon">{h.icon}</span>
                        <span>{h.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={goToday} style={{ background: '#e7f3ff', border: 'none', borderRadius: '6px', padding: '0.35rem 0.75rem', color: '#2c5364', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.75rem' }} data-testid="today-btn">
                {t('calendar.today')}
              </button>

              {/* Day headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', textAlign: 'center', marginBottom: '4px' }}>
                {dayNames.map(d => <div key={d} style={{ padding: '0.4rem', fontWeight: '700', color: '#6b8899', fontSize: '0.8rem' }}>{d}</div>)}
              </div>

              {/* Day cells */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} style={{ minHeight: '70px' }}></div>)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dayEvents = getEventsForDay(day);
                  const dayHolidays = getHolidaysForDay(day);
                  const isSelected = isSameDay(new Date(currentDate.getFullYear(), currentDate.getMonth(), day), selectedDate);
                  const todayFlag = isToday(day);
                  const isFederalHoliday = dayHolidays.some(h => h.type === 'federal');
                  return (
                    <div key={day} onClick={() => setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
                      data-testid={`day-cell-${day}`}
                      style={{
                        minHeight: '70px', padding: '4px', borderRadius: '6px', cursor: 'pointer',
                        border: isSelected ? '2px solid #2c5364' : isFederalHoliday ? '1px solid #dc2626' : '1px solid #e2e8f0',
                        background: todayFlag ? '#e7f3ff' : isFederalHoliday ? '#fff5f5' : isSelected ? '#f0f9ff' : '#fff',
                        transition: 'all 0.15s'
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: todayFlag ? '700' : '500', color: isFederalHoliday ? '#dc2626' : todayFlag ? '#2c5364' : '#374151', fontSize: '0.85rem' }}>{day}</span>
                        {dayHolidays[0] && (
                          <span title={dayHolidays.map(h => h.name).join(' • ')} style={{ fontSize: '0.95rem' }} data-testid={`holiday-icon-${day}`}>
                            {dayHolidays[0].icon}
                          </span>
                        )}
                      </div>
                      {dayHolidays[0] && (
                        <div style={{
                          fontSize: '0.6rem',
                          color: dayHolidays[0].type === 'federal' ? '#dc2626' : '#7c3aed',
                          fontWeight: 600,
                          marginBottom: '2px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {dayHolidays[0].name}
                        </div>
                      )}
                      {dayEvents.slice(0, 3).map(ev => (
                        <div key={ev.id} style={{
                          fontSize: '0.65rem', padding: '1px 4px', borderRadius: '3px', marginBottom: '1px',
                          background: EVENT_COLORS[ev.event_type] || '#6b7280', color: '#fff',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          textDecoration: ev.completed ? 'line-through' : 'none', opacity: ev.completed ? 0.6 : 1
                        }}>{ev.title}</div>
                      ))}
                      {dayEvents.length > 3 && <div style={{ fontSize: '0.6rem', color: '#6b8899' }}>+{dayEvents.length - 3}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sidebar: Selected day events */}
            <div>
              <div className="section-card" data-testid="day-events">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#2c5364', fontSize: '1rem' }}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </h3>
                  <button onClick={openAddForm} className="submit-button" style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.85rem' }} data-testid="add-event-btn">
                    <i className="fa-solid fa-plus"></i> {t('calendar.addEvent')}
                  </button>
                </div>

                {/* Holiday banner for selected day */}
                {(() => {
                  const selKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
                  const selHolidays = buildHolidayMap(selectedDate.getFullYear()).get(selKey) || [];
                  if (selHolidays.length === 0) return null;
                  return (
                    <div className="selected-day-holiday" data-testid="selected-day-holiday">
                      {selHolidays.map((h, i) => (
                        <div key={i} className={`selected-day-holiday-row holiday-${h.type}`}>
                          <span style={{ fontSize: '1.1rem' }}>{h.icon}</span>
                          <span><strong>{h.name}</strong></span>
                          <span className="holiday-type-pill">{h.type}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {getSelectedDayEvents().length === 0 ? (
                  <p style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem 0', fontSize: '0.9rem' }}>{t('calendar.noEvents')}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {getSelectedDayEvents().map(ev => (
                      <div key={ev.id} style={{
                        padding: '0.6rem', borderRadius: '8px', borderLeft: `4px solid ${EVENT_COLORS[ev.event_type] || '#6b7280'}`,
                        background: '#f8f9fa', opacity: ev.completed ? 0.6 : 1
                      }} data-testid={`event-${ev.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <input type="checkbox" checked={ev.completed} onChange={() => toggleComplete(ev)} data-testid={`complete-${ev.id}`} />
                              <span style={{ fontWeight: '600', color: '#2c5364', fontSize: '0.9rem', textDecoration: ev.completed ? 'line-through' : 'none' }}>{ev.title}</span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b8899', marginTop: '2px' }}>
                              {!ev.all_day && new Date(ev.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                              {ev.end_time && ` - ${new Date(ev.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                              {ev.all_day && t('calendar.allDay')}
                            </div>
                            {ev.location && <div style={{ fontSize: '0.75rem', color: '#6b8899' }}><i className="fa-solid fa-location-dot"></i> {ev.location}</div>}
                            {ev.notes && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{ev.notes}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => openEditForm(ev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '0.85rem' }} data-testid={`edit-event-${ev.id}`}>
                              <i className="fa-solid fa-pen"></i>
                            </button>
                            <button onClick={() => handleDelete(ev.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.85rem' }} data-testid={`delete-event-${ev.id}`}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Form */}
              {showForm && (
                <div className="section-card" style={{ marginTop: '1rem' }} data-testid="event-form">
                  <h3 style={{ margin: '0 0 1rem', color: '#2c5364' }}>{editingEvent ? t('calendar.editEvent') : t('calendar.addEvent')}</h3>
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.eventTitle')}</label>
                      <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="event-title-input" placeholder={t('calendar.eventTitle')} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.eventType')}</label>
                      <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })} data-testid="event-type-select">
                        <option value="appointment">{t('calendar.appointment')}</option>
                        <option value="task">{t('calendar.task')}</option>
                        <option value="medication">{t('calendar.medication')}</option>
                        <option value="event">{t('calendar.event')}</option>
                        <option value="other">{t('calendar.other')}</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input type="checkbox" checked={form.all_day} onChange={e => setForm({ ...form, all_day: e.target.checked })} id="allDay" data-testid="all-day-checkbox" />
                      <label htmlFor="allDay" style={{ margin: 0, fontSize: '0.9rem' }}>{t('calendar.allDay')}</label>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.startTime')}</label>
                      <input type={form.all_day ? 'date' : 'datetime-local'} value={form.start_time?.slice(0, form.all_day ? 10 : 16)} onChange={e => setForm({ ...form, start_time: e.target.value })} required data-testid="start-time-input" />
                    </div>
                    {!form.all_day && (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>{t('calendar.endTime')}</label>
                        <input type="datetime-local" value={form.end_time?.slice(0, 16) || ''} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="end-time-input" />
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.location')}</label>
                      <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder={t('calendar.location')} data-testid="location-input" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.reminder')}</label>
                      <select value={form.reminder_minutes || ''} onChange={e => setForm({ ...form, reminder_minutes: e.target.value ? parseInt(e.target.value) : null })} data-testid="reminder-select">
                        <option value="">{t('calendar.noReminder')}</option>
                        <option value="5">{t('calendar.min5')}</option>
                        <option value="15">{t('calendar.min15')}</option>
                        <option value="30">{t('calendar.min30')}</option>
                        <option value="60">{t('calendar.hour1')}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.recurring')}</label>
                      <select value={form.recurring || ''} onChange={e => setForm({ ...form, recurring: e.target.value || null })} data-testid="recurring-select">
                        <option value="">{t('calendar.none')}</option>
                        <option value="daily">{t('calendar.daily')}</option>
                        <option value="weekly">{t('calendar.weekly')}</option>
                        <option value="monthly">{t('calendar.monthly')}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('calendar.notes')}</label>
                      <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ resize: 'vertical' }} data-testid="notes-input" />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="submit" className="submit-button" style={{ flex: 1 }} data-testid="save-event-btn">
                        {editingEvent ? t('common.save') : t('calendar.addEvent')}
                      </button>
                      <button type="button" onClick={resetForm} style={{ flex: 1, padding: '0.6rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#6b8899' }} data-testid="cancel-event-btn">
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== TIMER TAB ===== */}
        {activeTab === 'timer' && (
          <div className="section-card" style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center' }} data-testid="timer-panel">
            <h2 style={{ color: '#2c5364', marginBottom: '1.5rem' }}><i className="fa-solid fa-hourglass-half"></i> {t('calendar.timer')}</h2>
            
            {!timerRunning && timerRemaining === 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  {[{ label: t('calendar.timerHours'), val: timerH, set: setTimerH, max: 23 },
                    { label: t('calendar.timerMinutes'), val: timerM, set: setTimerM, max: 59 },
                    { label: t('calendar.timerSeconds'), val: timerS, set: setTimerS, max: 59 }
                  ].map(({ label, val, set, max }) => (
                    <div key={label}>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b8899', marginBottom: '0.25rem' }}>{label}</label>
                      <input type="number" min={0} max={max} value={val} onChange={e => set(Math.min(max, Math.max(0, parseInt(e.target.value) || 0)))}
                        style={{ width: '70px', textAlign: 'center', padding: '0.75rem', fontSize: '1.5rem', borderRadius: '8px', border: '2px solid #e2e8f0' }}
                        data-testid={`timer-${label.toLowerCase()}`} />
                    </div>
                  ))}
                </div>
                <button onClick={startTimer} className="submit-button" style={{ width: 'auto', padding: '0.75rem 2rem' }} data-testid="timer-start-btn">
                  <i className="fa-solid fa-play"></i> {t('calendar.timerStart')}
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: '4rem', fontWeight: '700', fontFamily: 'monospace', color: timerRemaining <= 10 ? '#ef4444' : '#2c5364', marginBottom: '1.5rem', letterSpacing: '2px' }}
                  data-testid="timer-display">
                  {formatTimer(timerRemaining)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                  <button onClick={() => { setTimerRunning(!timerRunning); }} className="submit-button"
                    style={{ width: 'auto', padding: '0.75rem 2rem', background: timerRunning ? '#f59e0b' : '#2c5364' }}
                    data-testid="timer-pause-btn">
                    <i className={`fa-solid ${timerRunning ? 'fa-pause' : 'fa-play'}`}></i> {timerRunning ? t('calendar.timerPause') : t('calendar.timerStart')}
                  </button>
                  <button onClick={() => { clearInterval(timerRef.current); setTimerRunning(false); setTimerRemaining(0); }}
                    style={{ padding: '0.75rem 2rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#6b8899' }}
                    data-testid="timer-reset-btn">
                    <i className="fa-solid fa-rotate-left"></i> {t('calendar.timerReset')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ===== ALARM TAB ===== */}
        {activeTab === 'alarm' && (
          <div className="section-card" style={{ maxWidth: '480px', margin: '0 auto' }} data-testid="alarm-panel">
            <h2 style={{ color: '#2c5364', marginBottom: '1.5rem', textAlign: 'center' }}><i className="fa-solid fa-bell"></i> {t('calendar.alarm')}</h2>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
              <input type="time" value={alarmTime} onChange={e => setAlarmTime(e.target.value)}
                style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '1.1rem' }} data-testid="alarm-time-input" />
              <input type="text" value={alarmLabel} onChange={e => setAlarmLabel(e.target.value)} placeholder={t('calendar.alarmLabel')}
                style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', border: '2px solid #e2e8f0' }} data-testid="alarm-label-input" />
              <button onClick={addAlarm} className="submit-button" style={{ width: 'auto', padding: '0.75rem 1rem' }} data-testid="set-alarm-btn">
                <i className="fa-solid fa-plus"></i>
              </button>
            </div>

            {alarms.length === 0 ? (
              <p style={{ color: '#94a3b8', textAlign: 'center' }}>No alarms set</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {alarms.map(a => (
                  <div key={a.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.75rem 1rem', borderRadius: '8px', background: a.fired ? '#f1f5f9' : '#f0fdf4',
                    border: `1px solid ${a.fired ? '#e2e8f0' : '#86efac'}`
                  }} data-testid={`alarm-${a.id}`}>
                    <div>
                      <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#2c5364', fontFamily: 'monospace' }}>{a.time}</span>
                      <span style={{ marginLeft: '0.75rem', color: '#6b8899' }}>{a.label}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px', background: a.fired ? '#fee2e2' : '#dcfce7', color: a.fired ? '#dc2626' : '#16a34a' }}>
                        {a.fired ? 'Fired' : t('calendar.alarmActive')}
                      </span>
                      <button onClick={() => removeAlarm(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }} data-testid={`remove-alarm-${a.id}`}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== INTEGRATIONS / SYNC TAB ===== */}
        {activeTab === 'integrations' && (
          <div style={{ maxWidth: '720px', margin: '0 auto' }} data-testid="integrations-panel">
            <div className="section-card" style={{ marginBottom: '1rem' }}>
              <h2 style={{ color: '#2c5364', marginBottom: '0.25rem' }}>
                <i className="fa-solid fa-link"></i> Calendar Sync
              </h2>
              <p style={{ color: '#6b8899', marginTop: 0, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                Connect your personal calendar so CareGuard events sit alongside your appointments.
              </p>

              {/* Google Calendar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.75rem' }} data-testid="integration-google">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: 44, height: 44, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'grid', placeItems: 'center' }}>
                    <i className="fa-brands fa-google" style={{ color: '#4285F4', fontSize: '1.4rem' }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#2c5364' }}>Google Calendar</div>
                    <div style={{ fontSize: '0.82rem', color: '#6b8899' }}>
                      {integrations?.google?.connected
                        ? <>Connected as <strong>{integrations.google.email}</strong>{integrations.google.last_synced_at && <> · last synced {new Date(integrations.google.last_synced_at).toLocaleString()}</>}</>
                        : integrations?.google?.configured === false
                          ? 'Admin setup required (OAuth credentials missing)'
                          : 'Two-way sync · pull events + push CareGuard events'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {integrations?.google?.connected ? (
                    <>
                      <button className="submit-button" style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem' }} disabled={syncing} onClick={syncPullGoogle} data-testid="google-pull-btn">
                        <i className="fa-solid fa-download"></i> {syncing ? 'Syncing…' : 'Pull events'}
                      </button>
                      <button className="submit-button" style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem', background: '#2c5364' }} disabled={syncing} onClick={syncPushGoogle} data-testid="google-push-btn">
                        <i className="fa-solid fa-upload"></i> {syncing ? 'Syncing…' : 'Push events'}
                      </button>
                      <button className="submit-button" style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem', background: '#dc2626' }} onClick={disconnectGoogle} data-testid="google-disconnect-btn">
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button className="submit-button" style={{ width: 'auto', padding: '0.55rem 1.1rem', fontSize: '0.9rem' }} onClick={connectGoogle} disabled={integrations?.google?.configured === false} data-testid="google-connect-btn">
                      <i className="fa-brands fa-google"></i> Connect Google
                    </button>
                  )}
                </div>
              </div>

              {/* Outlook (coming soon) */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '10px', opacity: 0.75, flexWrap: 'wrap', gap: '0.75rem' }} data-testid="integration-outlook">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: 44, height: 44, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'grid', placeItems: 'center' }}>
                    <i className="fa-brands fa-microsoft" style={{ color: '#0078D4', fontSize: '1.4rem' }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#2c5364' }}>Microsoft Outlook</div>
                    <div style={{ fontSize: '0.82rem', color: '#6b8899' }}>Coming soon — Azure AD credentials required</div>
                  </div>
                </div>
                <button className="submit-button" style={{ width: 'auto', padding: '0.55rem 1.1rem', fontSize: '0.9rem', background: '#94a3b8', cursor: 'not-allowed' }} disabled data-testid="outlook-connect-btn">
                  Coming soon
                </button>
              </div>

              {integrations?.google?.configured === false && (
                <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '0.85rem', color: '#78350f' }}>
                  <i className="fa-solid fa-triangle-exclamation"></i> Google OAuth isn't configured yet. Admin must add <code>GOOGLE_CLIENT_ID</code> and <code>GOOGLE_CLIENT_SECRET</code> to backend env.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== CLOCK TAB ===== */}
        {activeTab === 'clock' && (
          <div className="section-card" style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center' }} data-testid="clock-panel">
            <h2 style={{ color: '#2c5364', marginBottom: '1rem' }}><i className="fa-solid fa-clock"></i> {t('calendar.clock')}</h2>
            <div style={{ fontSize: '4.5rem', fontWeight: '700', fontFamily: 'monospace', color: '#2c5364', letterSpacing: '3px', marginBottom: '0.5rem' }} data-testid="clock-display">
              {clockTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ fontSize: '1.1rem', color: '#6b8899' }}>
              {clockTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
